import { BleClient } from "@capacitor-community/bluetooth-le";
import {
  clearWifi,
  resetSequences,
  sendCommand,
  sendGetWifiList,
  sendGetWifiStatus,
  setWifi,
} from "@/features/ble/utils/sendCommand.ts";
import { AxiosInstance } from "axios";
import {
  getDeviceNotificationsStream,
  resetNotificationStreams,
} from "@/features/ble/utils/getDeviceNotificationsStream.tsx";
import { firstValueFrom, timeout } from "rxjs";
import { ref } from "vue";
import {
  removeBlePool,
  useStoredBlePool,
} from "@/features/ble/utils/localStorage.ts";
import { TechnicalError } from "@/shared/utils/error";

const clients = ref<Record<string, AxiosInstance>>({});

export const getBleClient = (
  deviceId: string,
  storedBlePool: ReturnType<typeof useStoredBlePool>
) => {
  if (!clients.value[deviceId]) {
    console.log("Creating ble client for device", deviceId);
    clients.value[deviceId] = {
      post: async (url: string, data: any) => {
        if (url === "/WaitCommand.php") {
          console.log("wait command skipped");
          return Promise.resolve({
            data: {
              response: {
                status: 0,
              },
            },
          });
        }

        let resp: any;
        try {
          console.log(`connecting to ${deviceId}`);
          try {
            await BleClient.connect(
              deviceId,
              () => {
                console.log(`${deviceId} disconnected`);
                resetNotificationStreams();
                resetSequences();
              },
              { timeout: 3000 }
            );
          } catch (e: any) {
            console.log(`Connection failed (${e.message}), retrying`);
            await BleClient.connect(deviceId, () => {
              console.log(`${deviceId} disconnected`);
              resetNotificationStreams();
              resetSequences();
            });
          }
          console.log(`connected to ${deviceId}`);
          const { responses$ } = getDeviceNotificationsStream(deviceId);

          if (url === "/ble:ClearWifi") {
            await clearWifi(deviceId);
            return { data: { response: null } };
          }

          // Subscribe before sending the command to avoid a race condition where
          // the device responds faster than we subscribe
          const responsePromise = firstValueFrom(
            responses$.pipe(timeout(15000))
          );

          console.log(`sending command ${url} to ${deviceId}`);
          if (url === "/ble:GetWifiList") {
            await sendGetWifiList(deviceId);
          } else if (url === "/ble:GetWifiStatus") {
            await sendGetWifiStatus(deviceId);
          } else if (url === "/ble:SetWifi") {
            await setWifi(deviceId, data);
          } else {
            if (url !== "/GetBleToken.php") {
              if (!storedBlePool.value?.token) {
                throw new Error("Missing ble token");
              }

              data = {
                ...data,
                token: storedBlePool.value?.token,
              };
            }

            await sendCommand(deviceId, url, data);
          }
          console.log("waiting for resp");
          resp = await responsePromise;
          console.log("got resp");
        } catch (e: any) {
          // Connection / write / timeout failures raised by the native BLE
          // layer or rxjs carry raw English strings ("Timeout", "Writing
          // Characteristic", ...). Tag them so they surface the generic message
          // instead of leaking, while preserving the message so the known
          // timeout cases can still be mapped downstream.
          console.log("myerror", e);
          throw new TechnicalError(e?.message ?? String(e), { cause: e });
        }

        // Device-provided business error: already meaningful, surfaced as-is.
        if (resp.status === "error") {
          console.log(resp);
          if (resp.detail === "NOT AUTHORIZED") {
            removeBlePool(deviceId);
          }
          throw new Error(resp.detail);
        }
        return { data: resp };
      },
    } as AxiosInstance;
  }

  return clients.value[deviceId];
};
